import { useState, useEffect, useCallback } from 'react';
import {
  Table, Badge, Text, Group, Pagination, Select, SegmentedControl, Stack,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import PageTemplate from '@/components/PageTemplate';
import EventDrawer from '@/components/EventDrawer';
import intakeEventMutations from '@/api/mutitations/intakeEvent';
import prepareTranslate from '@/helpers/dictionary';
import classes from './style.module.css';

const PAGE_SIZE = 20;

const EVENT_TYPE_COLOR = {
  filename_problem: 'orange',
  sheet_name_problem: 'yellow',
  import_problem: 'red',
  new_agent_created: 'blue',
};

const Events = () => {
  const t = prepareTranslate();

  const ERROR_CODE_OPTIONS = [
    { value: '', label: t('pages.events.filters.all') },
    { value: 'NO_COMPANY', label: t('pages.events.errorCodes.NO_COMPANY') },
    { value: 'NO_YEAR', label: t('pages.events.errorCodes.NO_YEAR') },
    { value: 'NO_MONTH', label: t('pages.events.errorCodes.NO_MONTH') },
    { value: 'NO_REFERENCE_YEAR', label: t('pages.events.errorCodes.NO_REFERENCE_YEAR') },
    { value: 'AMBIGUOUS', label: t('pages.events.errorCodes.AMBIGUOUS') },
    { value: 'NO_RULE', label: t('pages.events.errorCodes.NO_RULE') },
    { value: 'SHEET_NOT_FOUND', label: t('pages.events.errorCodes.SHEET_NOT_FOUND') },
    { value: 'MISSING_FILE', label: t('pages.events.errorCodes.MISSING_FILE') },
    { value: 'LOCKED_FILE', label: t('pages.events.errorCodes.LOCKED_FILE') },
    { value: 'UNREADABLE_FILE', label: t('pages.events.errorCodes.UNREADABLE_FILE') },
    { value: 'IMPORT_RULE_FAILURE', label: t('pages.events.errorCodes.IMPORT_RULE_FAILURE') },
    { value: 'PROCESS_FAILURE', label: t('pages.events.errorCodes.PROCESS_FAILURE') },
  ];

  const STATUS_OPTIONS = [
    { value: 'open', label: t('pages.events.status.open') },
    { value: 'closed', label: t('pages.events.status.closed') },
    { value: 'all', label: t('pages.events.filters.all') },
  ];

  const [events, setEvents] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [drawerOpened, { open: openDrawer, close: closeDrawer }] = useDisclosure(false);
  const [filters, setFilters] = useState({
    status: 'open',
    errorCode: '',
  });

  const { mutateAsync: getEvents } = intakeEventMutations.useGetAllIntakeEvents();

  const fetchEvents = useCallback(async (currentPage, currentFilters) => {
    const query = {};
    if (currentFilters.status !== 'all') query.status = currentFilters.status;
    if (currentFilters.errorCode) query.errorCode = currentFilters.errorCode;

    const skip = (currentPage - 1) * PAGE_SIZE;
    try {
      const result = await getEvents({ start: skip + 1, end: skip + PAGE_SIZE, query });
      const list = result || [];
      setEvents(list);
      setHasMore(list.length === PAGE_SIZE);
    } catch {
      notifications.show({
        title: t('pages.events.notification.fetchError.title'),
        message: t('pages.events.notification.fetchError.message'),
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchEvents(page, filters);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filters]);

  const handleRowClick = (event) => {
    setSelectedEvent(event);
    openDrawer();
  };

  const handleSuccess = () => {
    closeDrawer();
    setSelectedEvent(null);
    fetchEvents(page, filters);
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  return (
    <PageTemplate title={t('pages.events.title')}>
      <Stack gap="md">
        <Group gap="md" wrap="wrap">
          <Select
            placeholder={t('pages.events.filters.errorCode')}
            data={ERROR_CODE_OPTIONS}
            value={filters.errorCode}
            onChange={(v) => handleFilterChange('errorCode', v || '')}
            clearable
            w={200}
          />
          <SegmentedControl
            data={STATUS_OPTIONS}
            value={filters.status}
            onChange={(v) => handleFilterChange('status', v)}
          />
        </Group>

        {events.length === 0 ? (
          <Text c="dimmed" ta="center" mt="xl">{t('pages.events.empty')}</Text>
        ) : (
          <Table highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('pages.events.columns.date')}</Table.Th>
                <Table.Th>{t('pages.events.columns.type')}</Table.Th>
                <Table.Th>{t('pages.events.columns.errorCode')}</Table.Th>
                <Table.Th>{t('pages.events.columns.filename')}</Table.Th>
                <Table.Th>{t('pages.events.columns.user')}</Table.Th>
                <Table.Th>{t('pages.events.columns.status')}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {events.map((event) => (
                <Table.Tr
                  key={event._id}
                  onClick={() => handleRowClick(event)}
                  className={`${classes.row}${selectedEvent?._id === event._id ? ` ${classes.rowSelected}` : ''}`}
                >
                  <Table.Td>
                    {new Date(event.createdAt).toLocaleString('he-IL')}
                  </Table.Td>
                  <Table.Td>
                    <Badge color={EVENT_TYPE_COLOR[event.eventType] || 'gray'} size="sm">
                      {t(`pages.events.eventTypes.${event.eventType}`)}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      {event.errorCode ? (t(`pages.events.errorCodes.${event.errorCode}`) || event.errorCode) : '—'}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" truncate maw={200}>
                      {event.fileRef?.originalName || '—'}
                    </Text>
                  </Table.Td>
                  <Table.Td>{event.userName}</Table.Td>
                  <Table.Td>
                    <Badge
                      color={event.status === 'open' ? 'red' : 'green'}
                      size="sm"
                      variant="light"
                    >
                      {event.status === 'open' ? t('pages.events.status.open') : t('pages.events.status.closed')}
                    </Badge>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}

        <Group justify="center">
          <Pagination
            value={page}
            onChange={setPage}
            total={hasMore ? page + 1 : page}
          />
        </Group>
      </Stack>

      <EventDrawer
        event={selectedEvent}
        opened={drawerOpened}
        onClose={() => { closeDrawer(); setSelectedEvent(null); }}
        onSuccess={handleSuccess}
      />
    </PageTemplate>
  );
};

export default Events;
